import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { FmHeader, FmNavigatorMenu, FmSummary, FmMobileHeader, FmEditPopup } from "../../Components";
import { LANGUAGES_BY_KEYS, LOGGED_IN_ROUTES } from "../../Constants";
import { useIsLaptop, useIsMobile } from "../../Hooks";
import { AgriSolutionState, GeneralState, RootState } from "../../Redux";
import useStyles from "./FmLayout.style";
import { SavedItemsState, SolutionState } from "../../Redux/States";
import { ZERO } from "../../Utils/Consts";
import { agriSolution } from "../../Redux/States/AgriSolution/AgriSolutionSlice";

interface FmLayoutProps {
    children?: React.ReactNode;
    mobileHeaderRightButtonText?: string;
    mobileHeaderRightButtonOnClick?: () => void;
}
const FmLayout = ({ children, mobileHeaderRightButtonText, mobileHeaderRightButtonOnClick }: FmLayoutProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();
    const isRtl = useSelector(GeneralState.Selectors.selectIsRtl);
    const user = useSelector(GeneralState.Selectors.selectUser);
    const products = useSelector(GeneralState.Selectors.selectProducts);
    const greenBar = useSelector(GeneralState.Selectors.selectGreenBarState);
    const isMobile = useIsMobile();
    const isLaptop = useIsLaptop();
    const { metricMethods, activeMetricMethod } = useSelector(GeneralState.Selectors.selectMetricMethods);
    const activeLanguage = useSelector(GeneralState.Selectors.selectActiveLanguage);
    const selectedYield = useSelector(AgriSolutionState.Selectors.selectYield);
    const currentSavedItemName = useSelector(GeneralState.Selectors.selectCurrentVisibleSavedItemName);
    const currentSavedItemId = useSelector(GeneralState.Selectors.selectCurrentVisibleSavedItemId);

    const { t } = useTranslation();
    const [summaryOpen, setSummaryOpen] = useState<boolean>(!isLaptop && !isMobile);
    const [navigationIsOpen, setNavigationIsOpen] = useState<boolean>(!isLaptop && !isMobile);
    const showMobileMap = useSelector(AgriSolutionState.Selectors.selectShowMobileMap);

    const numberOfSavedItems = useSelector(SavedItemsState.Selectors.selectNumberOfSavedItems);

    const isAgri = useSelector(GeneralState.Selectors.selectIsAgri);

    useEffect(() => {
        const open = !isLaptop && !isMobile;
        setSummaryOpen(open);
        setNavigationIsOpen(open);
    }, [isLaptop, isMobile]);

    const metricMethodOptions = useMemo(
        () =>
            metricMethods.map((m) => ({
                key: m,
                value: t(`metricMethods.${m}`),
            })),
        [t, metricMethods]
    );

    const languageOptions = useMemo(() => {
        const languagesArray: { key: string; value: string }[] = [];

        for (const key in LANGUAGES_BY_KEYS) {
            languagesArray.push({ key: key, value: (LANGUAGES_BY_KEYS as any)[key] });
        }

        return languagesArray;
    }, []);

    const measureUnit = useSelector((state: RootState) => state.general.activeMetricMethod);

    const handleUnitChange = (selectedUnit: string) => {
        console.log("rabeea", { measureUnit });
        dispatch(GeneralState.Actions.setActiveMetricMethod(selectedUnit));

        if (selectedYield.length > ZERO) {
            const convertedYield = selectedUnit == "1" ? selectedYield[0].value : selectedYield[1].value;
            dispatch(AgriSolutionState.Actions.setExpectedYield(convertedYield));
        }
    };

    const handleLanguageChange = (newLanguage: string) => dispatch(GeneralState.Thunks.setLanguage(newLanguage as any));

    const handleNameClick = async () => {
        dispatch(GeneralState.Actions.setGreenBar({ isShow: false, lastIsShow: true }));
        await dispatch(AgriSolutionState.Thunks.getRawMaterialBank());
        history.push(LOGGED_IN_ROUTES.USER_AREA);
    };

    const rightBtnText = isMobile && showMobileMap ? t("general.close") : mobileHeaderRightButtonText;

    const handleRightButtonClick = rightBtnText
        ? () => {
              if (isMobile && showMobileMap) {
                  dispatch(AgriSolutionState.Actions.setShowMobileMap(false));
              } else {
                  mobileHeaderRightButtonOnClick && mobileHeaderRightButtonOnClick();
              }
          }
        : undefined;

    const onDelete = async () => {
        await dispatch(SavedItemsState.Thunks.deleteSaveItemById(currentSavedItemId));
        dispatch(GeneralState.Actions.setGreenBar({ isShow: false, lastIsShow: true }));
        history.push(LOGGED_IN_ROUTES.SAVED_ITEMS);
    };

    const onUpdate = async () => {
        if (isAgri) {
            dispatch(AgriSolutionState.Actions.setIsUserSave(true));
            await dispatch(AgriSolutionState.Thunks.saveProcess());
        } else {
            dispatch(SolutionState.Actions.setIsUserSave(true));
            await dispatch(SolutionState.Thunks.saveProcess());
        }
    };
    return (
        <div
            className={
                !greenBar.isShow
                    ? classnames(classes.container, classes.paddingTopClass, {
                          [classes.noScroll]: navigationIsOpen && isMobile,
                      })
                    : classnames(classes.editMode_container, {
                          [classes.noScroll]: navigationIsOpen && isMobile,
                      })
            }
        >
            {greenBar.isShow && (
                <div className={classes.editPopup_container}>
                    <FmEditPopup title={currentSavedItemName} onDelete={onDelete} onUpdate={onUpdate} />
                </div>
            )}
            {!isMobile && <FmSummary isEditMode={greenBar.isShow} isOpen={summaryOpen} setIsOpen={setSummaryOpen} products={products} />}
            {!isMobile && (
                <div
                    className={
                        greenBar.isShow
                            ? classnames(classes.headerContainer, {
                                  [classes.rtlHeader]: isRtl,
                                  [classes.headerWithEdit]: metricMethodOptions,
                              })
                            : classnames(classes.headerContainer, {
                                  [classes.rtlHeader]: isRtl,
                              })
                    }
                >
                    <FmHeader
                        units={metricMethodOptions}
                        selectedUnit={activeMetricMethod}
                        onUnitSelect={handleUnitChange}
                        languages={languageOptions}
                        selectedLanguage={activeLanguage}
                        onLanguageChange={handleLanguageChange}
                        fullName={user?.name || ""}
                        onNameClick={handleNameClick}
                        isEditMode={greenBar.isShow}
                    />
                </div>
            )}
            {isMobile && (
                <FmMobileHeader
                    leftIconStyle={"hamburger"}
                    rightButtonText={rightBtnText}
                    rightButtonOnClick={handleRightButtonClick}
                    onLeftButtonClick={() => setNavigationIsOpen(true)}
                    addShadow
                />
            )}
            <FmNavigatorMenu
                isOpen={navigationIsOpen}
                setIsOpen={setNavigationIsOpen}
                units={metricMethodOptions}
                selectedUnit={activeMetricMethod}
                onUnitSelect={handleUnitChange}
                languages={languageOptions}
                selectedLanguage={activeLanguage}
                onLanguageChange={handleLanguageChange}
                fullName={user?.name || ""}
                onNameClick={handleNameClick}
                numberOfSavedItems={numberOfSavedItems}
            />

            <div className={classes.content}>{children}</div>
        </div>
    );
};

export default FmLayout;
